import { useEffect, useMemo, useState } from "react";
import { Box, Button, LinearProgress } from "@mui/material";

import BasicTable from "../../components/shared/Table";
import {
  InsightsEmptyState,
  InsightsTableSkeleton,
} from "../../components/Insights/InsightsAsyncState";
import {
  AGGREGATE_TABLE_EMPHASIZED_COLUMN_KEYS,
  AGGREGATE_TABLE_PAGE_SIZE_OPTIONS,
} from "./constants";
import { AGGREGATE_TABLE_SORTABLE_COLUMN_KEYS } from "./pricingReportAggregateSort";

const emphasizedCellFontSx = { fontWeight: 700 };

function shouldEmphasizeCell(columnKey) {
  return AGGREGATE_TABLE_EMPHASIZED_COLUMN_KEYS.has(columnKey);
}

function wrapEmphasizedCell(columnKey, content) {
  if (!shouldEmphasizeCell(columnKey)) return content;
  return (
    <Box component="span" sx={emphasizedCellFontSx}>
      {content}
    </Box>
  );
}

function ReportTable({
  columns,
  rows,
  serverPagination,
  sortBy = "",
  sortDirection = "asc",
  onSortChange,
  isLoading = false,
  isRefreshing,
  onCountClick,
  onProductClick,
  onProvinceClick,
  onRetailerClick,
}) {
  const isServerPaged = Boolean(serverPagination);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const visibleColumns = useMemo(
    () => columns.filter((column) => column.key !== "provider"),
    [columns]
  );
  const linkButtonSx = {
    textTransform: "none",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    textAlign: "left",
    whiteSpace: "normal",
    lineHeight: 1.25,
    minWidth: 0,
    p: 0,
  };
  const getLinkButtonSx = (columnKey) =>
    shouldEmphasizeCell(columnKey)
      ? { ...linkButtonSx, ...emphasizedCellFontSx }
      : linkButtonSx;

  useEffect(() => {
    if (!isServerPaged) setTablePage(0);
  }, [rows, isServerPaged]);

  const basicTableHeaders = useMemo(
    () =>
      visibleColumns.map((column) => ({
        id: column.key,
        key: column.key,
        name: column.label,
        disabled: isServerPaged
          ? !AGGREGATE_TABLE_SORTABLE_COLUMN_KEYS.has(column.key)
          : true,
      })),
    [visibleColumns, isServerPaged]
  );

  const basicTableItems = useMemo(
    () =>
      rows.map((row, rowIndex) => {
        const rowId = `${visibleColumns.map((column) => String(row[column.key] ?? "")).join("|")}|${rowIndex}`;
        const item = { id: rowId };
        for (const column of visibleColumns) {
          const cellValue = row[column.key];
          if (column.key === "n" && typeof onCountClick === "function" && Number(cellValue) > 0) {
            item[column.key] = (
              <Button
                size="small"
                variant="text"
                sx={getLinkButtonSx(column.key)}
                onClick={(event) => {
                  event.stopPropagation();
                  onCountClick(row);
                }}
                aria-label={`Open ${cellValue} price history records`}
              >
                {cellValue}
              </Button>
            );
          } else if (
            column.key === "productName" &&
            typeof onProductClick === "function" &&
            row?.drilldown?.productId
          ) {
            item[column.key] = (
              <Button
                size="small"
                variant="text"
                sx={getLinkButtonSx(column.key)}
                onClick={(event) => {
                  event.stopPropagation();
                  onProductClick(row.drilldown.productId);
                }}
                aria-label={`Filter by product ${cellValue}`}
              >
                {cellValue}
              </Button>
            );
          } else if (
            column.key === "province" &&
            typeof onProvinceClick === "function" &&
            row?.drilldown?.province
          ) {
            item[column.key] = (
              <Button
                size="small"
                variant="text"
                sx={getLinkButtonSx(column.key)}
                onClick={(event) => {
                  event.stopPropagation();
                  onProvinceClick(row.drilldown.province);
                }}
                aria-label={`Filter by province ${cellValue}`}
              >
                {cellValue}
              </Button>
            );
          } else if (
            column.key === "retailer" &&
            typeof onRetailerClick === "function" &&
            row?.drilldown?.retailer
          ) {
            item[column.key] = (
              <Button
                size="small"
                variant="text"
                sx={getLinkButtonSx(column.key)}
                onClick={(event) => {
                  event.stopPropagation();
                  onRetailerClick(row.drilldown.retailer);
                }}
                aria-label={`Filter by retailer ${cellValue}`}
              >
                {cellValue}
              </Button>
            );
          } else if (column.format) {
            item[column.key] = wrapEmphasizedCell(column.key, column.format(cellValue));
          } else if (cellValue == null || cellValue === "") {
            item[column.key] = wrapEmphasizedCell(column.key, "—");
          } else {
            item[column.key] = wrapEmphasizedCell(column.key, cellValue);
          }
        }
        return item;
      }),
    [rows, visibleColumns, onCountClick, onProductClick, onProvinceClick, onRetailerClick, linkButtonSx]
  );

  const pagedTableItems = useMemo(() => {
    if (isServerPaged) return basicTableItems;
    const start = tablePage * rowsPerPage;
    return basicTableItems.slice(start, start + rowsPerPage);
  }, [basicTableItems, tablePage, rowsPerPage, isServerPaged]);

  const paginationPage = isServerPaged ? serverPagination.page : tablePage;
  const paginationRowsPerPage = isServerPaged ? serverPagination.rowsPerPage : rowsPerPage;
  const paginationTotalCount = isServerPaged ? serverPagination.totalCount : basicTableItems.length;
  const handlePaginationPage = isServerPaged
    ? serverPagination.onPageChange
    : (_, nextPage) => setTablePage(nextPage);
  const handlePaginationRowsPerPage = isServerPaged
    ? serverPagination.onRowsPerPageChange
    : (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setTablePage(0);
      };

  const showLoadingState = isLoading && !rows.length;
  const showEmptyState =
    !showLoadingState && !rows.length && (!isServerPaged || paginationTotalCount === 0);

  if (showLoadingState) {
    return <InsightsTableSkeleton sx={{ py: 1 }} />;
  }

  if (showEmptyState) {
    return (
      <InsightsEmptyState message="No rows for this report with the current filters." />
    );
  }

  const aggregateTableCellSx = {
    whiteSpace: "normal",
    wordBreak: "break-word",
    minWidth: 0,
  };
  const aggregateTablePaginationSx = {
    ".MuiTablePagination-toolbar": {
      flexWrap: "wrap",
      gap: 1,
      px: { xs: 0, sm: 2 },
    },
    ".MuiTablePagination-spacer": {
      display: "none",
    },
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%", overflowX: "hidden" }}>
      {isRefreshing ? <LinearProgress sx={{ mb: 1 }} /> : null}
      <BasicTable
        headers={basicTableHeaders}
        items={pagedTableItems}
        page={paginationPage}
        rowsPerPage={paginationRowsPerPage}
        totalCount={paginationTotalCount}
        handleChangePage={handlePaginationPage}
        handleChangeRowsPerPage={handlePaginationRowsPerPage}
        handleSort={typeof onSortChange === "function" ? onSortChange : undefined}
        sortBy={sortBy}
        sortDirection={sortDirection}
        tableSx={{ minWidth: 720 }}
        paginationSx={aggregateTablePaginationSx}
        cellSx={aggregateTableCellSx}
        containTableInScrollContainer
        rowsPerPageOptions={AGGREGATE_TABLE_PAGE_SIZE_OPTIONS}
      />
    </Box>
  );
}

export default ReportTable;
